import { Fragment, useCallback, useEffect, useMemo, useState, createElement } from "react";
import { createRoot, Root } from "react-dom/client";
import { IAccreditationEvent, IEventApcap, IUser } from "../@types/system";
import { Button } from "./Button";
import api from "../service/api";
import { toast } from "react-toastify";
import { Transition, Dialog } from "@headlessui/react";
import * as Input from "../components/Form/Input";
import * as Select from "../components/Form/Select";
import { useLoading } from "../store/useLoading";
import { useStore } from "../store";
import { Plus } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Select2 from "./select2";
import { BadgeLabel } from "./badge-label";
import {
  openBadgePrintWindow,
  wrapBadgeAsPrintPage,
} from "../utils/badgePrint";
import { hasPermission } from "../utils/helpers";
import {
  formatCpfOrCnpj,
  isValidCpfOrCnpj,
  normalizeDocumentDigits,
} from "../lib/utils/document";

interface IProps {
  data: IEventApcap;
  closeCredentials: (val: boolean) => void;
  idEvent: string;
}

const TableCredentials = ({ data, closeCredentials, idEvent }: IProps) => {
  const setLoading = useLoading((store) => store.setLoading);
  const user = useStore((store) => store.user);
  // Estado para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openModalPrint, setOpenModalPrint] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [selectedCredentials, setSelectedCredentials] = useState<string[]>([]);

  const [search, setSearch] = useState("");
  const [secretariaFilter, setSecretariaFilter] = useState("");

  // Dados para adicionar um novo credenciamento
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [document, setDocument] = useState("");
  const [idParticipant, setIdParticipant] = useState("");
  const [nameInsitution, setNameInstitution] = useState("");
  const [camaraId, setCamaraId] = useState("");
  const [listUsers, setListUsers] = useState<IUser[]>([]);

  const [accreditationUser, setAccreditationUser] = useState<
    IAccreditationEvent[]
  >([]);

  const secretarias = useMemo(() => {
    const values = accreditationUser
      .map((item) => item.departament?.trim())
      .filter((value): value is string => !!value);
    return Array.from(new Set(values)).sort((a, b) =>
      a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
  }, [accreditationUser]);

  const list = useMemo(() => {
    const byName = (a: IAccreditationEvent, b: IAccreditationEvent) =>
      a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" });

    const searchTerm = search.trim().toLowerCase();

    return accreditationUser
      .filter((data) => {
        const matchesSecretaria = (() => {
          if (!secretariaFilter) return true;
          if (secretariaFilter === "__NONE__") {
            return !(data.departament || "").trim();
          }
          return (data.departament || "") === secretariaFilter;
        })();

        if (!matchesSecretaria) return false;

        if (!searchTerm) return true;

        return (
          data.user?.name?.toLowerCase().includes(searchTerm) ||
          data.name.toLowerCase().includes(searchTerm) ||
          (data.document || "").toLowerCase().includes(searchTerm) ||
          (data.departament || "").toLowerCase().includes(searchTerm)
        );
      })
      .sort(byName);
  }, [search, secretariaFilter, accreditationUser]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, secretariaFilter]);

  // Cálculo para paginação
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = list.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(list.length / itemsPerPage) || 1;

  const handleData = useCallback(async () => {
    try {
      const resp = await api.get(`events/accreditation/event/${idEvent}`);
      setAccreditationUser(resp.data);
    } catch (error) {
      console.log("[ERROR]: ", error);
    }
  }, [idEvent]);

  async function handleGetUsers() {
    try {
      const resp = await api.get(`users`);
      setListUsers(resp.data);
    } catch (error) {
      console.log("[ERROR]: ", error);
    }
  }

  useEffect(() => {
    handleData();
    handleGetUsers();
  }, [handleData]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const addNewAccredited = useCallback(async () => {
    const documentDigits = normalizeDocumentDigits(document);

    if (!name || !position || !isValidCpfOrCnpj(documentDigits)) {
      toast.error("Preencha nome, cargo e um CPF ou CNPJ válido", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    setLoading(true);

    try {
      await api.post("events/accreditation", {
        user_id:
          user.user_type === "ADMIN" || user.user_type === "SUPERADMIN"
            ? camaraId
            : user.id,
        event_id: idEvent,
        name,
        position,
        document: documentDigits,
      });

      // Fechar modal de adicionar e abrir modal de confirmação
      setOpenModalAdd(false);
      setOpenModalConfirm(true);
      handleData();
    } catch (error) {
      console.log(error);
      const apiMessage =
        (
          error as { response?: { data?: { message?: string; error?: string } } }
        )?.response?.data?.message ||
        (
          error as { response?: { data?: { message?: string; error?: string } } }
        )?.response?.data?.error ||
        "Erro ao tentar criar credenciamento";
      toast.error(apiMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  }, [
    name,
    position,
    document,
    handleData,
    idEvent,
    setLoading,
    camaraId,
    user,
  ]);

  const editAccredited = useCallback(async () => {
    const documentDigits = normalizeDocumentDigits(document);

    if (!name || !position || !idParticipant || !isValidCpfOrCnpj(documentDigits)) {
      toast.error("Preencha nome, cargo e um CPF ou CNPJ válido", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    setLoading(true);

    try {
      await api.put("events/accreditation", {
        user_id:
          user.user_type === "ADMIN" || user.user_type === "SUPERADMIN"
            ? camaraId
            : user.id,
        id: idParticipant,
        event_id: idEvent,
        name,
        position,
        document: documentDigits,
      });

      toast.success("Credenciamento criado com sucesso", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } catch (error) {
      console.log(error);
      const apiMessage =
        (
          error as { response?: { data?: { message?: string; error?: string } } }
        )?.response?.data?.message ||
        (
          error as { response?: { data?: { message?: string; error?: string } } }
        )?.response?.data?.error ||
        "Erro ao tentar editar credenciamento";
      toast.error(apiMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } finally {
      setLoading(false);
      setOpenModalEdit(false);
      handleData();
    }
  }, [name, position, document, camaraId, idParticipant, idEvent, user.id, user.user_type, setLoading, handleData]);

  const closeModal = () => {
    setOpenModalAdd(false);
  };
  const closeModalAddNewCredential = () => {
    setOpenModalAdd(false);
    setName("");
    setPosition("");
    setDocument("");
    setCamaraId("");
  };

  const handleConfirmAddAnother = () => {
    setOpenModalConfirm(false);
    setName("");
    setPosition("");
    setDocument("");
    setCamaraId("");
    setOpenModalAdd(true);
  };

  const handleFinishAccreditation = () => {
    setOpenModalConfirm(false);
    setName("");
    setPosition("");
    setDocument("");
    setCamaraId("");
    toast.success("Credenciamentos realizados com sucesso!", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };
  const closeModalEdit = () => {
    setOpenModalEdit(false);
  };
  const closeModalEditNewCredential = () => {
    setOpenModalEdit(false);
  };
  const closeModalPrint = () => {
    setOpenModalPrint(false);
  };

  const showModalEdit = (data: IAccreditationEvent) => {
    console.log(data);
    setOpenModalEdit(true);
    setIdParticipant(data.id);
    setName(data.name);
    setPosition(data.position);
    setDocument(formatCpfOrCnpj(data.document || ""));
    setCamaraId(data.user_id);
  };
  const showModalPrint = (data: IAccreditationEvent) => {
    setOpenModalPrint(true);
    setIdParticipant(data.id);
    setName(data.name);
    setPosition(data.position);
    setDocument(formatCpfOrCnpj(data.document || ""));
    setNameInstitution(data.user?.name);
  };

  const handlePrint = () => {
    const divContent =
      window.document.getElementById("content-to-print")?.outerHTML || "";

    const opened = openBadgePrintWindow(divContent);
    if (!opened) {
      console.error("Não foi possível abrir a janela de impressão");
      toast.error("Não foi possível abrir a janela de impressão", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  };

  const handleToggleSelect = (credentialId: string) => {
    setSelectedCredentials((prev) =>
      prev.includes(credentialId)
        ? prev.filter((id) => id !== credentialId)
        : [...prev, credentialId]
    );
  };

  const handleSelectAll = () => {
    const currentItemIds = currentItems.map((item) => item.id);
    const allCurrentSelected = currentItemIds.every((id) =>
      selectedCredentials.includes(id)
    );

    if (allCurrentSelected) {
      // Desmarcar apenas os itens da página atual
      setSelectedCredentials((prev) =>
        prev.filter((id) => !currentItemIds.includes(id))
      );
    } else {
      // Marcar todos os itens da página atual (mantendo os de outras páginas)
      setSelectedCredentials((prev) => {
        const newSelection = [...prev];
        currentItemIds.forEach((id) => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handlePrintBatch = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    console.log("handlePrintBatch chamado", { selectedCredentials, accreditationUser });

    if (selectedCredentials.length === 0) {
      toast.error("Selecione pelo menos um credenciado para imprimir", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    // Buscar os dados dos credenciados selecionados
    const selectedData = accreditationUser.filter((cred) =>
      selectedCredentials.includes(cred.id)
    );

    console.log("selectedData", selectedData);

    if (selectedData.length === 0) {
      toast.error("Nenhum credenciado encontrado com os IDs selecionados", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    const count = selectedCredentials.length;
    console.log("Iniciando renderização de", count, "crachás");

    // Verificar se estamos no ambiente do navegador
    if (typeof window === "undefined" || typeof document === "undefined") {
      toast.error("Erro: ambiente não suportado para impressão", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return;
    }

    // Criar um container temporário oculto para renderizar os QR Codes
    const tempContainer = window.document.createElement("div");
    tempContainer.id = "batch-print-container";
    tempContainer.style.cssText =
      "position:fixed;left:-10000px;top:0;width:56mm;opacity:0;pointer-events:none;";
    window.document.body.appendChild(tempContainer);

    // Ordenar alfabeticamente para navegação previsível na impressão
    const orderedData = [...selectedData].sort((a, b) =>
      a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
    );

    const renderBadges = async (): Promise<string[]> => {
      const badgeElements: string[] = [];
      const roots: Root[] = [];

      for (let i = 0; i < orderedData.length; i++) {
        const credential = orderedData[i];
        const qrCodeUrl = `https://app.aprenderecapacitarbrasil.com.br/scanner/${credential.id}/${idEvent}`;

        const badgeDiv = window.document.createElement("div");
        badgeDiv.id = `badge-temp-${i}`;
        tempContainer.appendChild(badgeDiv);

        const root = createRoot(badgeDiv);
        roots.push(root);

        root.render(
          createElement(BadgeLabel, {
            name: credential.name,
            institution: credential.user?.name || "",
            qrValue: qrCodeUrl,
            previewQrSize: 68,
            showPaperFrame: false,
          })
        );
      }

      // Espera React + SVG do QR em cada crachá (importante no tablet)
      const maxWaitMs = 8000;
      const started = Date.now();
      while (Date.now() - started < maxWaitMs) {
        const ready = orderedData.every((_, i) => {
          const el = window.document.getElementById(`badge-temp-${i}`);
          return !!el?.querySelector("svg");
        });
        if (ready) break;
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      // frame extra para layout
      await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
      await new Promise((resolve) => setTimeout(resolve, 150));

      for (let i = 0; i < orderedData.length; i++) {
        const badgeDiv = window.document.getElementById(`badge-temp-${i}`);
        const badgeEl =
          badgeDiv?.querySelector(".badge") || badgeDiv?.firstElementChild;
        if (badgeEl) {
          badgeElements.push(wrapBadgeAsPrintPage(badgeEl.outerHTML));
        }
      }

      roots.forEach((root) => {
        try {
          root.unmount();
        } catch {
          // ignore
        }
      });

      return badgeElements;
    };

    renderBadges()
      .then((badgesHtml) => {
        try {
          if (window.document.body.contains(tempContainer)) {
            window.document.body.removeChild(tempContainer);
          }
        } catch (e) {
          console.warn("Erro ao remover container:", e);
        }

        if (badgesHtml.length === 0) {
          toast.error("Não foi possível gerar os crachás para impressão", {
            position: "top-right",
            autoClose: 5000,
            theme: "colored",
          });
          return;
        }

        const opened = openBadgePrintWindow(badgesHtml.join(""));
        if (!opened) {
          toast.error(
            "Não foi possível iniciar a impressão. Verifique o bloqueador de pop-ups.",
            {
              position: "top-right",
              autoClose: 5000,
              theme: "colored",
            }
          );
          return;
        }

        setSelectedCredentials([]);
        toast.success(
          `${badgesHtml.length} crachá(s) preparado(s) — confira o total de páginas na impressão`,
          {
            position: "top-right",
            autoClose: 4000,
            theme: "colored",
          }
        );
      })
      .catch((error) => {
        console.error("Erro ao gerar crachás:", error);
        try {
          if (window.document.body.contains(tempContainer)) {
            window.document.body.removeChild(tempContainer);
          }
        } catch (e) {
          console.warn("Erro ao remover container no catch:", e);
        }
        toast.error(
          "Erro ao gerar os crachás: " + (error.message || "Erro desconhecido"),
          {
            position: "top-right",
            autoClose: 5000,
            theme: "colored",
          }
        );
      });
  };

  return (
    <>
      <div className="overflow-x-auto rounded-t-lg">
        <div className="p-4 bg-white dark:bg-zinc-800">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
            <h2 className="text-xl font-bold text-gray-700 dark:text-white shrink-0">
              {data.name}
            </h2>

            {/* Filtros */}
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <input
                type="text"
                placeholder="Pesquisar nome, documento..."
                className="w-full min-h-[44px] p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apcap focus:border-apcap sm:w-56"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                value={secretariaFilter}
                onChange={(e) => setSecretariaFilter(e.target.value)}
                className="w-full min-h-[44px] rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-900 focus:border-apcap focus:outline-none focus:ring-2 focus:ring-apcap sm:w-56"
              >
                <option value="">Todas as secretarias</option>
                <option value="__NONE__">Sem secretaria</option>
                {secretarias.map((secretaria) => (
                  <option key={secretaria} value={secretaria}>
                    {secretaria}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                className="bg-apcap hover:bg-apcap-light text-white flex flex-row items-center gap-2 justify-center"
                onClick={() => setOpenModalAdd(true)}
              >
                <Plus size={16} /> participante
              </Button>
              {(user.user_type === "ADMIN" || user.user_type === "SUPERADMIN") && (
                <Button
                  type="button"
                  className={`${selectedCredentials.length > 0
                    ? "bg-green-500 hover:bg-green-600"
                    : "bg-gray-400 hover:bg-gray-500 cursor-not-allowed opacity-60"
                    } text-white flex flex-row items-center gap-2 justify-center`}
                  onClick={(e) => {
                    console.log("Botão clicado");
                    handlePrintBatch(e);
                  }}
                >
                  Imprimir Lote {selectedCredentials.length > 0 && `(${selectedCredentials.length})`}
                </Button>
              )}
              <Button variant="outline" onClick={() => closeCredentials(false)}>
                Voltar
              </Button>
            </div>
          </div>
        </div>
        <table className="min-w-full divide-y-2 divide-gray-200 bg-white text-sm text-left">
          <thead className="ltr:text-left rtl:text-right max-w-screen">
            <tr>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                {(user.user_type === "ADMIN" || user.user_type === "SUPERADMIN") && (
                  <input
                    type="checkbox"
                    checked={
                      currentItems.length > 0 &&
                      currentItems.every((item) =>
                        selectedCredentials.includes(item.id)
                      )
                    }
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-apcap focus:ring-apcap"
                  />
                )}
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                Nome
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                Documento
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                Cargo
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                Secretaria
              </th>
              <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                Credenciou
              </th>
              <th className="text-right whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                #
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentItems.length === 0 && (
              <tr>
                <td
                  colSpan={(user.user_type === "ADMIN" || user.user_type === "SUPERADMIN") ? 7 : 6}
                  className="text-center py-4 text-gray-500"
                >
                  Nenhum credenciamento encontrado
                </td>
              </tr>
            )}
            {currentItems.map((credential) => {
              return (
                <tr key={credential.id} className="hover:bg-gray-100">
                  <td className="px-4 py-2 font-medium text-gray-900">
                    {(user.user_type === "ADMIN" || user.user_type === "SUPERADMIN") && (
                      <input
                        type="checkbox"
                        checked={selectedCredentials.includes(credential.id)}
                        onChange={() => handleToggleSelect(credential.id)}
                        className="rounded border-gray-300 text-apcap focus:ring-apcap"
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 font-medium text-gray-900">
                    {credential.name}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {credential.document}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {credential.position}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {credential.departament || "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {credential.user?.name}
                  </td>
                  {/* {user?.user_type !== 'USER' && user?.user_type !== 'USERENTERPRISE' && ( */}
                  <td className="flex gap-2 justify-end px-4 py-2 text-gray-700">
                    <Button
                      variant="ghost"
                      className="font-bold text-green-500 hover:text-green-600 border"
                      onClick={() => showModalEdit(credential)}
                    >
                      Editar
                    </Button>
                    {(user.user_type === "ADMIN" ||
                      user.user_type === "SUPERADMIN") && (
                        <Button
                          variant="ghost"
                          className="font-bold text-green-500 hover:text-green-600 border"
                          onClick={() => showModalPrint(credential)}
                        >
                          Gerar crachá
                        </Button>
                      )}
                  </td>
                  {/* )} */}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-3 rounded-b-lg bg-white shadow-sm px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-2 items-center">
          <select
            name="qtd-pages"
            id="qtd-pages"
            className="inline-flex items-center justify-center rounded border 
    border-gray-100 bg-white text-gray-900 cursor-pointer"
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>

          <div className="text-gray-700"> de {list.length}</div>
        </div>

        <ol className="flex justify-end gap-1 text-xs font-medium">
          {/* Previous Button */}
          <li>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`inline-flex size-8 items-center justify-center rounded border border-gray-100 bg-white text-gray-900 rtl:rotate-180 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              <span className="sr-only">Prev Page</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </li>

          {/* Pagination Logic */}
          {(() => {
            const pageNumbers = [];
            let startPage = Math.max(1, currentPage - 2); // Start 2 pages before the current page
            let endPage = Math.min(totalPages, currentPage + 2); // End 2 pages after the current page

            if (currentPage <= 3) {
              startPage = 1;
              endPage = Math.min(totalPages, 5); // Always show the first 5 pages if near the start
            } else if (currentPage >= totalPages - 2) {
              startPage = Math.max(1, totalPages - 4); // Always show the last 5 pages if near the end
              endPage = totalPages;
            }

            // Add pages to display
            for (let i = startPage; i <= endPage; i++) {
              pageNumbers.push(
                <li key={i}>
                  <button
                    onClick={() => handlePageChange(i)}
                    className={`block size-8 rounded border border-gray-100 text-center leading-8 text-gray-900 ${currentPage === i ? "bg-apcap text-white" : ""
                      }`}
                  >
                    {i}
                  </button>
                </li>
              );
            }

            return (
              <>
                {/* Show "..." before the start if there are pages hidden */}
                {startPage > 1 && (
                  <li>
                    <span className="block size-8 text-center text-gray-500 pt-2">
                      ...
                    </span>
                  </li>
                )}

                {/* Render the page numbers */}
                {pageNumbers}

                {/* Show "..." after the end if there are pages hidden */}
                {endPage < totalPages && (
                  <li>
                    <span className="block size-8 text-center text-gray-500 pt-2">
                      ...
                    </span>
                  </li>
                )}
              </>
            );
          })()}

          {/* Next Button */}
          <li>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`inline-flex size-8 items-center justify-center rounded border border-gray-100 bg-white text-gray-900 rtl:rotate-180 ${currentPage === totalPages
                ? "opacity-50 cursor-not-allowed"
                : ""
                }`}
            >
              <span className="sr-only">Next Page</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </li>
        </ol>
      </div>

      {/* Criar um modal para adicionar um novo credenciado com os campos de nome, cargo e documento */}
      <Transition.Root show={openModalAdd} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-h-[90vh] transform overflow-y-auto rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-apcap-muted sm:mx-0 sm:h-10 sm:w-10">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-apcap"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6 text-gray-900"
                        >
                          Adicionar Credenciado
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Preencha os campos a baixo para adicionar um novo
                            credenciado
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      id="accredited"
                      className="mt-8 flex w-full flex-col gap-5 divide-y divide-zinc-200 dark:divide-zinc-800"
                    >
                      <div className="grid gap-3 lg:grid-cols-form">
                        <label
                          htmlFor="name"
                          className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
                        >
                          Nome
                        </label>
                        <div className="flex gap-3">
                          <Input.Root>
                            <Input.Control
                              name="name"
                              placeholder="Nome"
                              id="name"
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                            />
                          </Input.Root>
                        </div>
                      </div>
                      <div className="grid gap-3 lg:grid-cols-form pt-5">
                        <label
                          htmlFor="position"
                          className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
                        >
                          Cargo
                        </label>
                        <div className="flex gap-3">
                          <Select.Root
                            name="position"
                            value={position || undefined}
                            onValueChange={(value) => {
                              try {
                                setPosition(value || "");
                              } catch (error) {
                                console.error("Error setting position:", error);
                                setPosition("");
                              }
                            }}
                          >
                            <Select.Trigger>
                              <Select.Value placeholder="Selecione o cargo" />
                            </Select.Trigger>

                            <Select.Content>
                              <Select.Item value="SERVIDOR_PUBLICO">
                                <Select.ItemText>Servidor Público</Select.ItemText>
                              </Select.Item>
                              <Select.Item value="PARLAMENTAR">
                                <Select.ItemText>Parlamentar</Select.ItemText>
                              </Select.Item>
                            </Select.Content>
                          </Select.Root>
                        </div>
                      </div>

                      <div className="grid gap-3 lg:grid-cols-form pt-5">
                        <label
                          htmlFor="document"
                          className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
                        >
                          Documento *
                        </label>
                        <div className="flex gap-3">
                          <Input.Root>
                            <Input.Control
                              id="document"
                              type="text"
                              name="document"
                              inputMode="numeric"
                              placeholder="CPF ou CNPJ"
                              value={document}
                              onChange={(e) =>
                                setDocument(formatCpfOrCnpj(e.target.value))
                              }
                            />
                          </Input.Root>
                        </div>
                      </div>

                      {(user.user_type === "ADMIN" ||
                        user.user_type === "SUPERADMIN") && (
                          <div className="grid gap-3 lg:grid-cols-form pt-5">
                            <label
                              htmlFor="camara_id"
                              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
                            >
                              Câmaras
                            </label>
                            <div className="w-full">
                              <Select2
                                options={listUsers.map((camara) => ({
                                  value: camara.id,
                                  label: camara.name,
                                }))}
                                value={camaraId}
                                onChange={setCamaraId}
                                placeholder="Selecione uma câmara"
                                searchPlaceholder="Pesquisar câmara..."
                                clearable
                              />
                              {/* <Select.Root
                              name="camara_id"
                              value={selectedName}
                              onValueChange={setCamaraId}
                            >
                              <Select.Trigger>
                                <Select.Value placeholder="Selecione a Câmara" />
                              </Select.Trigger>

                              <Select.Content>
                                {listUsers.map(
                                  (camara, index) => (
                                    <Select.Item value={camara.id} key={index}>
                                      <Select.ItemText>{camara.name}</Select.ItemText>
                                    </Select.Item>
                                  )
                                )}
                              </Select.Content>
                            </Select.Root> */}
                            </div>
                          </div>
                        )}

                      <div className="flex items-center justify-end gap-2 pt-5">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={closeModalAddNewCredential}
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="submit"
                          form="users"
                          variant="primary"
                          onClick={addNewAccredited}
                        >
                          Salvar
                        </Button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={openModalEdit} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModalEdit}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-h-[90vh] transform overflow-y-auto rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-apcap-muted sm:mx-0 sm:h-10 sm:w-10">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-apcap"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6 text-gray-900"
                        >
                          Editar credenciado
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Preencha os campos a baixo para adicionar um novo
                            credenciado
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      id="accredited"
                      className="mt-8 flex w-full flex-col gap-5 divide-y divide-zinc-200 dark:divide-zinc-800"
                    >
                      <div className="grid gap-3 lg:grid-cols-form">
                        <label
                          htmlFor="name"
                          className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
                        >
                          Nome
                        </label>
                        <div className="flex gap-3">
                          <Input.Root>
                            <Input.Control
                              name="name"
                              placeholder="Nome"
                              id="name"
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                            />
                          </Input.Root>
                        </div>
                      </div>
                      <div className="grid gap-3 lg:grid-cols-form pt-5">
                        <label
                          htmlFor="position"
                          className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
                        >
                          Cargo
                        </label>
                        <div className="flex gap-3">
                          <Select.Root
                            name="position"
                            value={position || undefined}
                            onValueChange={(value) => {
                              try {
                                setPosition(value || "");
                              } catch (error) {
                                console.error("Error setting position:", error);
                                setPosition("");
                              }
                            }}
                          >
                            <Select.Trigger>
                              <Select.Value placeholder="Selecione o cargo" />
                            </Select.Trigger>

                            <Select.Content>
                              <Select.Item value="SERVIDOR_PUBLICO">
                                <Select.ItemText>Servidor Público</Select.ItemText>
                              </Select.Item>
                              <Select.Item value="PARLAMENTAR">
                                <Select.ItemText>Parlamentar</Select.ItemText>
                              </Select.Item>
                            </Select.Content>
                          </Select.Root>
                        </div>
                      </div>

                      <div className="grid gap-3 lg:grid-cols-form pt-5">
                        <label
                          htmlFor="document"
                          className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
                        >
                          Documento *
                        </label>
                        <div className="flex gap-3">
                          <Input.Root>
                            <Input.Control
                              id="document"
                              type="text"
                              name="document"
                              inputMode="numeric"
                              placeholder="CPF ou CNPJ"
                              value={document}
                              onChange={(e) =>
                                setDocument(formatCpfOrCnpj(e.target.value))
                              }
                            />
                          </Input.Root>
                        </div>
                      </div>
                      {(user.user_type === "ADMIN" ||
                        user.user_type === "SUPERADMIN") && (
                          <div className="grid gap-3 lg:grid-cols-form pt-5">
                            <label
                              htmlFor="camara_id"
                              className="text-sm font-medium text-zinc-700 dark:text-zinc-100"
                            >
                              Câmaras
                            </label>
                            <div className="w-full">
                              <Select2
                                options={listUsers.map((camara) => ({
                                  value: camara.id,
                                  label: camara.name,
                                }))}
                                value={camaraId}
                                onChange={setCamaraId}
                                placeholder="Selecione uma câmara"
                                searchPlaceholder="Pesquisar câmara..."
                                clearable
                              />
                              {/* <Select.Root
                              name="camara_id"
                              value={selectedName}
                              onValueChange={setCamaraId}
                            >
                              <Select.Trigger>
                                <Select.Value placeholder="Selecione a Câmara" />
                              </Select.Trigger>

                              <Select.Content>
                                {listUsers.map(
                                  (camara, index) => (
                                    <Select.Item value={camara.id} key={index}>
                                      <Select.ItemText>{camara.name}</Select.ItemText>
                                    </Select.Item>
                                  )
                                )}
                              </Select.Content>
                            </Select.Root> */}
                            </div>
                          </div>
                        )}

                      <div className="flex items-center justify-end gap-2 pt-5">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={closeModalEditNewCredential}
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="submit"
                          form="users"
                          variant="primary"
                          onClick={editAccredited}
                        >
                          Editar
                        </Button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      <Transition.Root show={openModalPrint} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModalPrint}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-h-[90vh] transform overflow-y-auto rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start border-b pb-4">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-apcap-muted sm:mx-0 sm:h-10 sm:w-10">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-apcap"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 9V2h12v7M6 18h12v4H6v-4zM6 14h12v-5H6v5zM8 18h8M9 9h6"
                          />
                        </svg>
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6 text-gray-900"
                        >
                          Imprimir Crachá
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Verifique os dados e clique em imprimir.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      id="generate-credentials"
                      className="mt-8 flex w-full flex-col gap-5 divide-y divide-zinc-200 dark:divide-zinc-800"
                    >
                      <div className="flex flex-col gap-3 justify-center items-center">
                        <h2 className="text-lgl font-bold text-zinc-700 text-center">
                          Impressão do crachá do {position}:{" "}
                          <p className="text-xl">{name}</p>
                        </h2>
                        <p className="text-xs text-zinc-500">Folha 60mm × 40mm</p>
                        <BadgeLabel
                          id="content-to-print"
                          name={name}
                          institution={nameInsitution}
                          qrValue={`https://app.aprenderecapacitarbrasil.com.br/scanner/${idParticipant}/${idEvent}`}
                        />
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-5">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={closeModalPrint}
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="submit"
                          form="users"
                          variant="primary"
                          onClick={handlePrint}
                        >
                          Imprimir
                        </Button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Modal de Confirmação */}
      <Transition.Root show={openModalConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setOpenModalConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-h-[90vh] transform overflow-y-auto rounded-lg bg-white dark:bg-zinc-800 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg">
                  <div className="bg-white dark:bg-zinc-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 sm:mx-0 sm:h-10 sm:w-10">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-green-600 dark:text-green-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                        >
                          Credenciamento realizado com sucesso!
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500 dark:text-zinc-400">
                            Deseja credenciar outro usuário?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-zinc-900/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleConfirmAddAnother}
                      className="w-full sm:ml-3 sm:w-auto"
                    >
                      Sim, credenciar outro
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleFinishAccreditation}
                      className="mt-3 w-full sm:mt-0 sm:w-auto"
                    >
                      Não, finalizar
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {!hasPermission(user.user_type) && (
        <div className="fixed bottom-6 right-6 z-50">
          <a
            href="https://wa.me/5581993354483?text=Solicitar%20nota%20fiscal"
            target="_blank"
            rel="noopener noreferrer"
            className="relative flex min-w-[120px] flex-col items-center justify-center gap-1 rounded-full bg-green-500 px-4 py-3 font-medium text-white shadow-lg transition-all duration-200 hover:scale-105 hover:bg-green-600 hover:shadow-xl"
            aria-label="Solicitar nota fiscal pelo WhatsApp"
          >
            <FaWhatsapp size={28} aria-hidden />
            <span className="text-center text-xs leading-tight sm:text-sm">
              Solicitar nota fiscal
            </span>
          </a>
        </div>
      )}
    </>
  );
};

export default TableCredentials;
